// @flow
import * as React from 'react';
import {useRef, useEffect} from 'react';
import {getActiveFile} from 'docs/util';
import {renderMarkdocAsReact} from 'docs/markdoc';
import Box from 'sail/Box';
import Group from 'sail/Group';
import classNames from 'classnames';
import {CaptionAlt} from 'sail/Text';
import Switch from 'sail/Switch';
import {useAnalytics} from 'docs/data';
import {useStep, useCondition, useFile} from 'docs/data/integration-builder';

import type {RenderNodes} from '@markdoc/markdoc';
import type {Condition} from 'docs/data/integration-builder';

import './Step.scss';
import {useContentFrame} from 'docs/shell/content/ContentFrameProvider';

const requiresScroll = (el?: HTMLDivElement) => {
  const NAV_OFFSET = 150;

  if (!el) {
    return false;
  }

  const {top, bottom} = el.getBoundingClientRect();
  return top < NAV_OFFSET || bottom > window.innerHeight - NAV_OFFSET;
};

type StepTitleProps = {|
  toggle: string,
  title?: string,
  stepEl: ?HTMLDivElement,
|};

const StepTitle = ({toggle, title, stepEl}: StepTitleProps) => {
  const {toggles, setToggleValue} = useCondition();
  const {setActiveStep} = useStep();
  const {scrollToStep} = useContentFrame();

  const {trackAction} = useAnalytics();

  if (!title) {
    return null;
  }

  if (!toggle) {
    // Ideally we would use the docs Heading component but it defaults
    // to rendering anchor tags -- this felt like the cleanest way around that
    return <h3 className="Heading">{title}</h3>;
  }

  return (
    <Group alignItems="center" direction="horizontal" spacing={8}>
      <Switch
        onChange={(evt) => {
          const step = !toggles[toggle] ? toggle : '';
          setActiveStep(step);
          setToggleValue(toggle, !toggles[toggle]);
          if (stepEl && requiresScroll(stepEl)) {
            const top = stepEl.offsetTop;
            scrollToStep(top - 20);
          }
          trackAction('integration_builder_toggle_extension', {
            extension_id: toggle,
            on: !toggles[toggle],
          });
        }}
        label={<h3 className="Heading">{title}</h3>}
        value={toggles[toggle]}
      />
    </Group>
  );
};

export type Label = 'client' | 'server' | 'none';

type StepProps = {|
  id: string,
  toggle: string,
  toggleDefaultOn: boolean,
  label: Label,
  when: Condition,
  unless: Condition,
  heading: string,
  paragraphs: RenderNodes,
  remainder: RenderNodes,
  children: Array<React.Element<any>>,
|};

export const Step = ({
  id,
  toggle = '',
  toggleDefaultOn = false,
  label = 'server',
  when,
  unless,
  heading,
  paragraphs,
  remainder,
  children,
}: StepProps) => {
  const {activeStep, setActiveStep} = useStep();
  const {activeFile, setActiveFile, components} = useFile();
  const {isVisible, toggles, setToggleValue} = useCondition();
  const {scrollToStep} = useContentFrame();
  const ref = useRef<null | HTMLDivElement>(null);
  const {trackAction} = useAnalytics();

  useEffect(() => {
    if (toggle && toggleDefaultOn && !(toggle in toggles)) {
      setToggleValue(toggle, toggleDefaultOn);
      trackAction('integration_builder_toggle_default_on', {
        extension_id: toggle,
        on: toggleDefaultOn,
      });
    }
  }, [toggle, toggleDefaultOn, toggles, setToggleValue, trackAction]);

  if (!isVisible({when, unless})) {
    return null;
  }

  const onClick = (evt) => {
    if (ref.current && requiresScroll(ref.current)) {
      const top = ref.current.offsetTop;
      scrollToStep(top - 20);
    }
    const fileWithStep = getActiveFile(components, id);
    if (fileWithStep && fileWithStep !== activeFile) {
      setActiveFile(fileWithStep);
    }
    setActiveStep(id);
  };

  return (
    <Box
      className={classNames(
        'Step',
        activeStep === id && 'Step--active',
        toggle && 'Step--toggle',
      )}
      boxRef={ref}
      data-id={id}
      margin={{top: 24}}
      onClick={onClick}
    >
      <Box
        className={classNames(
          'Step--container',
          toggle && 'Step--container--no-border',
        )}
        padding={{horizontal: 24, vertical: 20}}
      >
        <Group spacing={{vertical: 8}}>
          <StepTitle toggle={toggle} title={heading} stepEl={ref.current} />
          {renderMarkdocAsReact(paragraphs)}
          {renderMarkdocAsReact(remainder)}
          {label !== 'none' && (
            <Box flex={{alignItems: 'center'}}>
              <Box
                className={classNames('Step--icon', `Step--icon--${label}`)}
              />
              <Box>
                <CaptionAlt color="gray" style={{textTransform: 'capitalize'}}>
                  {label}
                </CaptionAlt>
              </Box>
            </Box>
          )}
        </Group>
      </Box>
    </Box>
  );
};
